import React from "react";
import { motion } from "framer-motion";
import { Clock, MapPin, Phone, Calendar } from "@phosphor-icons/react";
import { FIRM } from "../lib/practiceAreas";
import { BookingDialog } from "./BookingDialog";
import { scrollToId } from "../hooks/useLenis";

export const WalkIn = () => {
  const scrollTo = (id) => scrollToId(id);

  return (
    <section
      id="walk-in"
      data-testid="walkin-section"
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${FIRM.walkInBg})` }}
      />
      <div className="absolute inset-0 bg-[#0B0B0E]/85" />
      <div className="absolute inset-0 grain" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#E9C76A]" />
            <span className="overline">Complimentary</span>
          </div>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#F5F2E8]">
            Free walk-in sessions
            <br />
            <span className="italic text-[#E9C76A]">— no appointment needed.</span>
          </h2>
          <p className="mt-8 text-base md:text-lg text-white/65 max-w-2xl font-light leading-relaxed">
            Drop in to our Southall office for a complimentary, confidential
            discussion with a qualified solicitor. We&apos;ll listen, assess your
            position, and outline your options — at no cost.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <BookingDialog
              trigger={
                <button data-testid="walkin-book-btn" className="btn-gold inline-flex items-center gap-3">
                  Book Your Slot
                  <Calendar size={14} weight="bold" />
                </button>
              }
            />
            <a
              href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
              data-testid="walkin-call-btn"
              className="btn-ghost-gold inline-flex items-center gap-3"
            >
              Or call {FIRM.phone}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-14 grid md:grid-cols-3 gap-6"
        >
          <div className="border border-white/10 bg-black/40 backdrop-blur-md p-8 glass-card" data-testid="walkin-card-hours">
            <Clock size={32} weight="thin" className="text-[#E9C76A] mb-5" />
            <div className="overline mb-2">When</div>
            <div className="font-serif text-2xl text-[#F5F2E8] leading-snug">
              Monday – Friday
              <br />
              <span className="text-[#E9C76A]">9:30am – 4:30pm</span>
            </div>
          </div>
          <div className="border border-white/10 bg-black/40 backdrop-blur-md p-8 glass-card" data-testid="walkin-card-where">
            <MapPin size={32} weight="thin" className="text-[#E9C76A] mb-5" />
            <div className="overline mb-2">Where</div>
            <div className="font-serif text-xl text-[#F5F2E8] leading-snug">
              47 North Road, Southall<br />
              Middlesex UB1 2JL
            </div>
          </div>
          <div className="border border-white/10 bg-black/40 backdrop-blur-md p-8 glass-card" data-testid="walkin-card-call">
            <Phone size={32} weight="thin" className="text-[#E9C76A] mb-5" />
            <div className="overline mb-2">Call ahead</div>
            <div className="font-serif text-2xl text-[#F5F2E8] leading-snug">
              {FIRM.phone}
            </div>
            <button
              data-testid="walkin-cta"
              onClick={() => scrollTo("contact")}
              className="mt-5 text-xs uppercase tracking-[0.2em] text-[#E9C76A] hover:underline underline-offset-4"
            >
              Or send a message →
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
